import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { AlertCircleIcon, CalendarIcon, ShieldCheckIcon, ClockIcon } from 'lucide-react';
interface CancellationPolicyPageProps {
  onNavigate: (page: string) => void;
}
export function CancellationPolicyPage({
  onNavigate
}: CancellationPolicyPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="flex-grow w-full bg-brand-gray" data-id="element-630">
      <PageHeader title="Cancellation Policy" breadcrumb="Cancellation Policy" onNavigateHome={() => onNavigate('home')} data-id="element-631" />

      <section className="py-16 md:py-24" data-id="element-632">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-633">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-634">
            {/* Key Highlight */}
            <div className="bg-brand-navy p-6 md:p-8" data-id="element-635">
              <div className="flex items-start gap-4" data-id="element-636">
                <div className="w-12 h-12 rounded-xl bg-brand-red/20 flex items-center justify-center shrink-0" data-id="element-637">
                  <AlertCircleIcon className="w-6 h-6 text-brand-red" data-id="element-638" />
                </div>
                <div data-id="element-639">
                  <h2 className="text-xl font-bold text-white mb-2" data-id="element-640">
                    48-Hour Cancellation Window
                  </h2>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-641">
                    All cancellations must be made at least 48 hours before your
                    rental start date to receive a full refund.
                  </p>
                </div>
              </div>
            </div>

            {/* Policy Content */}
            <div className="p-6 md:p-8 space-y-8" data-id="element-642">
              <div data-id="element-643">
                <p className="text-brand-charcoal/80 leading-relaxed" data-id="element-644">
                  If for any reason you need to cancel your reservation with us,
                  we simply require that you cancel your reservation{' '}
                  <strong className="text-brand-navy" data-id="element-645">48 hours prior</strong> to
                  your rental date. If you fail to cancel your reservation 48
                  hours prior to your rental date, Scooterplus will retain the
                  rental amount.
                </p>
              </div>

              {/* Example Box */}
              <div className="bg-brand-gray rounded-xl p-5 border border-gray-100" data-id="element-646">
                <div className="flex items-center gap-2 mb-3" data-id="element-647">
                  <CalendarIcon className="w-5 h-5 text-brand-red" data-id="element-648" />
                  <h3 className="font-bold text-brand-navy" data-id="element-649">Example</h3>
                </div>
                <p className="text-brand-charcoal/70 text-sm leading-relaxed" data-id="element-650">
                  If your rental date is{' '}
                  <strong className="text-brand-navy" data-id="element-651">08/11/2010</strong>, your
                  reservation must be canceled by{' '}
                  <strong className="text-brand-navy" data-id="element-652">08/09/2010</strong> to
                  receive a full refund.
                </p>
              </div>

              {/* Late Cancellation */}
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-100" data-id="element-653">
                <div className="flex items-center gap-2 mb-3" data-id="element-654">
                  <ClockIcon className="w-5 h-5 text-amber-600" data-id="element-655" />
                  <h3 className="font-bold text-brand-navy" data-id="element-656">
                    Late Cancellation (Within 48 Hours)
                  </h3>
                </div>
                <p className="text-brand-charcoal/70 text-sm leading-relaxed" data-id="element-657">
                  As a courtesy to our customers, if for any reason you request
                  cancellation within 48 hours of your rental start date and you
                  did not purchase our cancellation protection plan, you will
                  receive a{' '}
                  <strong className="text-brand-navy" data-id="element-658">
                    credit to be used towards a future rental
                  </strong>
                  . The credit must be used within{' '}
                  <strong className="text-brand-navy" data-id="element-659">180 days</strong>.
                </p>
              </div>

              {/* Protection Plan */}
              <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100" data-id="element-660">
                <div className="flex items-center gap-2 mb-3" data-id="element-661">
                  <ShieldCheckIcon className="w-5 h-5 text-emerald-600" data-id="element-662" />
                  <h3 className="font-bold text-brand-navy" data-id="element-663">
                    Cancellation Protection Plan
                  </h3>
                </div>
                <p className="text-brand-charcoal/70 text-sm leading-relaxed" data-id="element-664">
                  Consider purchasing our cancellation protection plan at
                  checkout for added flexibility. With this plan, you can cancel
                  at any time and receive a full refund regardless of the
                  cancellation window.
                </p>
              </div>

              {/* Quick Summary */}
              <div className="border-t border-gray-100 pt-8" data-id="element-665">
                <h3 className="font-bold text-brand-navy mb-4" data-id="element-666">
                  Quick Summary
                </h3>
                <div className="space-y-3" data-id="element-667">
                  <div className="flex items-start gap-3" data-id="element-668">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-669">
                      <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-670">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-671" />
                      </svg>
                    </div>
                    <p className="text-sm text-brand-charcoal/80" data-id="element-672">
                      <strong className="text-brand-navy" data-id="element-673">
                        Cancel 48+ hours before
                      </strong>{' '}
                      → Full refund
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-674">
                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-675">
                      <ClockIcon className="w-3.5 h-3.5 text-amber-600" data-id="element-676" />
                    </div>
                    <p className="text-sm text-brand-charcoal/80" data-id="element-677">
                      <strong className="text-brand-navy" data-id="element-678">
                        Cancel within 48 hours (no protection plan)
                      </strong>{' '}
                      → Rental credit valid for 180 days
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-679">
                    <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-680">
                      <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-681">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-682" />
                      </svg>
                    </div>
                    <p className="text-sm text-brand-charcoal/80" data-id="element-683">
                      <strong className="text-brand-navy" data-id="element-684">
                        No cancellation / no-show
                      </strong>{' '}
                      → Rental amount retained
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact CTA */}
              <div className="border-t border-gray-100 pt-8 text-center" data-id="element-685">
                <p className="text-brand-charcoal/60 text-sm mb-4" data-id="element-686">
                  Have questions about our cancellation policy?
                </p>
                <button onClick={() => onNavigate('contact')} className="inline-flex items-center justify-center px-8 py-3 bg-brand-red text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-md" data-id="element-687">
                  Contact Us
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>;
}